/**
 * Custom Product List – scrollbar / progress bar
 * Binds a custom progress bar to carousel scrollables in [data-custom-scrollbar] sections.
 * Fully functional: scroll sync, track click-to-jump, thumb drag.
 */

(function () {
  const ROOT = '[data-custom-scrollbar]';
  /** Only the list carousel slideshow (scrolls product cards), not card-gallery slideshows (product images). */
  const LIST_CAROUSEL_SLIDESHOW = '.resource-list__carousel > slideshow-component';
  const SCROLLER = 'slideshow-slides';

  /**
   * @param {number} min
   * @param {number} val
   * @param {number} max
   * @returns {number}
   */
  function clamp(min, val, max) {
    return Math.min(Math.max(val, min), max);
  }

  /**
   * @param {Element} container
   * @returns {(() => void) | null}
   */
  function createScrollbar(container) {
    const scrollerEl = container.querySelector(SCROLLER);
    if (!scrollerEl) return null;
    const scroller = /** @type {Element} */ (scrollerEl);

    const track = document.createElement('div');
    track.className = 'cpl-scrollbar__track';
    track.setAttribute('role', 'scrollbar');
    track.setAttribute('aria-valuenow', '0');
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-label', 'Carousel scroll position');

    const thumb = document.createElement('div');
    thumb.className = 'cpl-scrollbar__thumb';
    track.appendChild(thumb);

    const wrap = document.createElement('div');
    wrap.className = 'cpl-scrollbar';
    wrap.appendChild(track);
    container.appendChild(wrap);

    /** @type {number | null} */
    let raf = null;
    let dragging = false;
    let dragStartX = 0;
    let dragStartScroll = 0;

    function getScrollMetrics() {
      const sw = scroller.scrollWidth;
      const cw = scroller.clientWidth;
      const sl = scroller.scrollLeft;
      const maxScroll = Math.max(0, sw - cw);
      return { sw, cw, sl, maxScroll };
    }

    function update() {
      const { sw, cw, sl, maxScroll } = getScrollMetrics();
      const canScroll = maxScroll > 1;

      if (!canScroll) {
        wrap.removeAttribute('data-visible');
        return;
      }

      wrap.setAttribute('data-visible', '');

      const trackRect = track.getBoundingClientRect();
      const trackW = trackRect.width;
      const thumbW = Math.max(24, (cw / sw) * trackW);
      const range = Math.max(0, trackW - thumbW);
      const pct = maxScroll > 0 ? sl / maxScroll : 0;
      const left = range * clamp(0, pct, 1);

      thumb.style.width = `${thumbW}px`;
      thumb.style.transform = `translateX(${left}px)`;
      track.setAttribute('aria-valuenow', String(Math.round(pct * 100)));
    }

    function scheduleUpdate() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        update();
      });
    }

    /**
     * @param {number} fraction
     */
    function scrollToFraction(fraction) {
      const { maxScroll } = getScrollMetrics();
      if (maxScroll <= 0) return;
      scroller.scrollTo({
        left: clamp(0, fraction * maxScroll, maxScroll),
        behavior: 'auto'
      });
    }

    /**
     * @param {MouseEvent} e
     */
    function handleTrackClick(e) {
      if (dragging) return;
      const t = /** @type {Element | null} */ (e.target);
      if (!t || t.closest('.cpl-scrollbar__thumb')) return;

      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const frac = clamp(0, x / rect.width, 1);
      scrollToFraction(frac);
    }

    /**
     * @param {MouseEvent} e
     */
    function handleThumbMouseDown(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      dragging = true;
      dragStartX = e.clientX;
      dragStartScroll = scroller.scrollLeft;

      /**
       * @param {MouseEvent} e2
       */
      const onMove = (e2) => {
        const dx = e2.clientX - dragStartX;
        const { maxScroll } = getScrollMetrics();
        if (maxScroll <= 0) return;
        const trackW = track.getBoundingClientRect().width;
        const thumbW = parseFloat(thumb.style.width) || 24;
        const range = Math.max(0, trackW - thumbW);
        const deltaFrac = range > 0 ? dx / range : 0;
        const startFrac = maxScroll > 0 ? dragStartScroll / maxScroll : 0;
        scrollToFraction(clamp(0, startFrac + deltaFrac, 1));
      };

      const onUp = () => {
        dragging = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }

    /**
     * @param {TouchEvent} e
     */
    function handleThumbTouchStart(e) {
      if (e.touches.length !== 1) return;
      const touch0 = e.touches[0];
      if (!touch0) return;
      dragging = true;
      let prevX = touch0.clientX;
      let prevScroll = scroller.scrollLeft;

      /**
       * @param {TouchEvent} e2
       */
      const onTouchMove = (e2) => {
        const t = e2.touches[0];
        if (!t) return;
        const dx = t.clientX - prevX;
        prevX = t.clientX;
        const { maxScroll } = getScrollMetrics();
        if (maxScroll <= 0) return;
        const trackW = track.getBoundingClientRect().width;
        const thumbW = parseFloat(thumb.style.width) || 24;
        const range = Math.max(0, trackW - thumbW);
        const deltaFrac = range > 0 ? dx / range : 0;
        const startFrac = maxScroll > 0 ? prevScroll / maxScroll : 0;
        const next = clamp(0, startFrac + deltaFrac, 1);
        scrollToFraction(next);
        prevScroll = next * maxScroll;
      };

      const onTouchEnd = () => {
        dragging = false;
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      document.addEventListener('touchmove', onTouchMove, { passive: true });
      document.addEventListener('touchend', onTouchEnd);
    }

    track.addEventListener('click', handleTrackClick);
    thumb.addEventListener('mousedown', handleThumbMouseDown);
    thumb.addEventListener('touchstart', handleThumbTouchStart, { passive: true });

    scroller.addEventListener('scroll', scheduleUpdate, { passive: true });

    const ro = new ResizeObserver(scheduleUpdate);
    ro.observe(scroller);

    update();

    return function destroy() {
      scroller.removeEventListener('scroll', scheduleUpdate);
      ro.disconnect();
      track.removeEventListener('click', handleTrackClick);
      thumb.removeEventListener('mousedown', handleThumbMouseDown);
      thumb.removeEventListener('touchstart', handleThumbTouchStart);
      wrap.remove();
      if (raf) cancelAnimationFrame(raf);
    };
  }

  function init() {
    const roots = document.querySelectorAll(ROOT);
    roots.forEach((root) => {
      const listSlideshows = root.querySelectorAll(LIST_CAROUSEL_SLIDESHOW);
      listSlideshows.forEach((el) => {
        if (el.querySelector('.cpl-scrollbar')) return;
        createScrollbar(el);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
